import React, { useState, useEffect } from 'react';
import { FileJson, ExternalLink, Copy, Check } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';
const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function OpenApiDocsPage() {
  const [specUrl] = useState(`${API_BASE}/openapi.json`);
  const [products, setProducts] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Fetch spec to extract product tags
    api.get<{ tags?: { name: string }[] }>('/openapi.json')
      .then(spec => {
        if (spec.tags) {
          setProducts(spec.tags.map(t => t.name));
        }
      })
      .catch(() => {});
  }, []);

  const currentSpecUrl = selectedProduct === 'all'
    ? `${API_BASE}/openapi.json`
    : `${API_BASE}/openapi/${selectedProduct}.json`;

  const fullSpecUrl = selectedProduct === 'all'
    ? `${window.location.origin}${API_BASE}/openapi.json`
    : `${window.location.origin}${API_BASE}/openapi/${selectedProduct}.json`;

  const handleCopy = () => {
    navigator.clipboard.writeText(fullSpecUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>API Documentation</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
            Interactive API reference powered by OpenAPI 3.1
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {products.length > 0 && (
            <select
              value={selectedProduct}
              onChange={e => setSelectedProduct(e.target.value)}
              style={{
                padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem',
                fontSize: '0.875rem', backgroundColor: '#fff',
              }}
            >
              <option value="all">All Products</option>
              {products.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}

          <button
            onClick={handleCopy}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5rem 0.75rem',
              backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db',
              borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.813rem',
            }}
          >
            {copied ? <Check size={14} style={{ color: '#22c55e' }} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy URL'}
          </button>

          <a
            href={currentSpecUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5rem 0.75rem',
              backgroundColor: ACCENT, color: '#fff', borderRadius: '0.375rem',
              textDecoration: 'none', fontSize: '0.813rem', fontWeight: 500,
            }}
          >
            <FileJson size={14} /> Raw JSON <ExternalLink size={12} />
          </a>
        </div>
      </div>

      <div style={{
        backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
        padding: '2rem', display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
        justifyContent: 'center', gap: '1rem', height: 'calc(100vh - 220px)',
      }}>
        <FileJson size={48} style={{ color: '#9ca3af' }} />
        <p style={{ color: '#374151', fontSize: '1rem', fontWeight: 500, margin: 0 }}>
          Open the API docs in a new tab to view the interactive Swagger UI
        </p>
        <a
          href={`${API_BASE}/docs${selectedProduct !== 'all' ? `?product=${selectedProduct}` : ''}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem',
            backgroundColor: ACCENT, color: '#fff', borderRadius: '0.375rem',
            textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500,
          }}
        >
          Open API Docs <ExternalLink size={14} />
        </a>
      </div>
    </div>
  );
}
